import moment from 'moment-timezone';
import { Dispatch } from 'redux';
import { listDocumentExtractions } from '../../clients/HrpsExtractionsApi';
import {
    ExtractionDisplayStatus,
    ExtractionFilters,
    ExtractionSortAttribute,
    ExtractionSortOrder,
    ListDocumentExtractionsOutput,
} from '../../types';
import { toStatusFilter } from '../../utils/extractionUtils';
import { ExtractionListState, ExtractionSortDirection, RootState, SearchableExtractionFields } from '../types';
import {
    EXTRACTION_LIST_ERROR,
    EXTRACTION_LIST_FILTER_CHANGE,
    EXTRACTION_LIST_GET,
    EXTRACTION_LIST_LOADED,
    EXTRACTION_LIST_PAGE_CHANGE,
    EXTRACTION_LIST_SORT_CHANGE,
    ExtractionListActions,
} from './extractionListActions';

export function extractionListLoading(): ExtractionListActions {
    return { type: EXTRACTION_LIST_GET };
}

export function extractionListLoaded(data: ListDocumentExtractionsOutput): ExtractionListActions {
    return { type: EXTRACTION_LIST_LOADED, payload: data };
}

export function extractionListFailed(error: any): ExtractionListActions {
    return { type: EXTRACTION_LIST_ERROR, payload: { error } };
}

export function extractionListChangePage(currentPage: number, startAt: number): ExtractionListActions {
    return { type: EXTRACTION_LIST_PAGE_CHANGE, payload: { currentPage, startAt } };
}

export function extractionListSortChange(
    sortColumn: string,
    sortDirection: ExtractionSortDirection
): ExtractionListActions {
    return { type: EXTRACTION_LIST_SORT_CHANGE, payload: { sortColumn, sortDirection } };
}

export function extractionListFilterChange(filters: {
    statusFilter?: ExtractionDisplayStatus[];
    dateRange?: string[];
    query?: string;
    searchBy?: SearchableExtractionFields;
}): ExtractionListActions {
    return { type: EXTRACTION_LIST_FILTER_CHANGE, payload: filters };
}

function toSortAttribute(sortColumn: string): ExtractionSortAttribute {
    switch (sortColumn) {
        case 'documentType':
            return 'DOCUMENT_TYPE';
        case 'customerReference':
            return 'CUSTOMER_REFERENCE';
        case 'fileName':
            return 'FILE_NAME';
        case 'requestedBy':
            return 'REQUESTED_BY';
        case 'status':
            return 'STATUS';
        default:
            return 'CREATED_AT';
    }
}

export function buildExtractionFilters(state: ExtractionListState, timezone?: string): ExtractionFilters | undefined {
    const tz = timezone || 'UTC';
    const { statusFilter, dateRange, query, searchBy } = state;

    const filters: ExtractionFilters = {
        statuses: toStatusFilter(statusFilter),
        createdAfter: dateRange[0] ? moment.tz(dateRange[0], tz).startOf('day').unix() : undefined,
        createdBefore: dateRange[1] ? moment.tz(dateRange[1], tz).endOf('day').unix() : undefined,
    };

    if (query) {
        switch (searchBy) {
            case SearchableExtractionFields.fileName:
                filters.fileName = query;
                break;
            case SearchableExtractionFields.requestedBy:
                filters.requestedBy = query;
                break;
            default:
                filters.customerReference = query;
                break;
        }
    }

    return Object.values(filters).some((value) => value !== undefined) ? filters : undefined;
}

export function getExtractionList(resellerId: string, startAt: number, maxResults: number) {
    return (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const { sortColumn, sortDirection } = state.extractionListReducer;
        const timezone = state.resellerReducer.resellerDetails.value?.resellerIanaTimezone || undefined;
        const sortOrder: ExtractionSortOrder = sortDirection === 'ascending' ? 'ASCENDING' : 'DESCENDING';

        dispatch(extractionListLoading());

        return listDocumentExtractions({
            resellerId,
            maxResults,
            startAtResult: startAt,
            sortBy: toSortAttribute(sortColumn),
            sortOrder,
            filters: buildExtractionFilters(state.extractionListReducer, timezone),
        })
            .then((output) => {
                dispatch(extractionListLoaded(output));
            })
            .catch((error) => {
                dispatch(extractionListFailed(error));
            });
    };
}
